import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { useThemeContext } from '../../contexts/ThemeContext';

// Material UI Components
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useScrollTrigger,
  Slide,
  alpha,
} from '@mui/material';

// Material UI Icons
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';

/**
 * Material UI Navbar component for public pages
 * @returns {React.ReactNode}
 */
const Navbar = () => {
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const { primaryColor } = useThemeContext();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Hide navbar on scroll down, show on scroll up
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });
  
  // Close mobile drawer when route changes
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);
  
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };
  
  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  const handleLogout = () => {
    handleCloseUserMenu();
    logout();
    navigate('/');
  };
  
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      <AppBar 
        position="sticky"
        elevation={0}
        sx={{ 
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: { xs: '56px', sm: '64px' }, py: 0.5 }}>
            {/* Logo - Desktop */}
            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontWeight: 700,
                background: primaryColor ? `linear-gradient(90deg, ${primaryColor}, ${adjustColor(primaryColor, 20)})` : 'linear-gradient(90deg, #3b82f6, #6366f1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textDecoration: 'none',
              }}
            >
              {tenant ? tenant.name : 'Membership Portal'}
            </Typography>

            {/* Mobile menu icon */}
            <Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="open menu"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleDrawerToggle}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
            </Box>
            
            {/* Logo - Mobile */}
            <Typography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                flexGrow: 1,
                display: { xs: 'flex', md: 'none' },
                fontWeight: 700,
                background: primaryColor ? `linear-gradient(90deg, ${primaryColor}, ${adjustColor(primaryColor, 20)})` : 'linear-gradient(90deg, #3b82f6, #6366f1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textDecoration: 'none',
              }}
            >
              {tenant ? tenant.name : 'MP'}
            </Typography>
            
            {/* Desktop navigation */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, ml: 4 }}>
              <Button
                component={RouterLink}
                to="/"
                sx={{ 
                  my: 2, 
                  px: 2,
                  color: location.pathname === '/' ? primaryColor || 'primary.main' : 'text.primary',
                  display: 'block',
                  fontWeight: location.pathname === '/' ? 600 : 500,
                  position: 'relative',
                  '&::after': location.pathname === '/' ? {
                    content: '""',
                    position: 'absolute',
                    width: '30%',
                    height: '3px',
                    bottom: 0,
                    left: '35%',
                    backgroundColor: primaryColor || 'primary.main',
                    borderRadius: '2px',
                  } : {},
                }}
              >
                Home
              </Button>
              <Button
                component={RouterLink}
                to="/marketing-materials"
                sx={{ 
                  my: 2, 
                  px: 2,
                  color: location.pathname === '/marketing-materials' ? primaryColor || 'primary.main' : 'text.primary',
                  display: 'block',
                  fontWeight: location.pathname === '/marketing-materials' ? 600 : 500,
                  position: 'relative',
                  '&::after': location.pathname === '/marketing-materials' ? {
                    content: '""',
                    position: 'absolute',
                    width: '30%',
                    height: '3px',
                    bottom: 0,
                    left: '35%',
                    backgroundColor: primaryColor || 'primary.main',
                    borderRadius: '2px',
                  } : {},
                }}
              >
                Marketing Materials
              </Button>
              {user && (
                <>
                  <Button
                    component={RouterLink}
                    to="/subscription"
                    sx={{ 
                      my: 2, 
                      px: 2,
                      color: location.pathname === '/subscription' ? primaryColor || 'primary.main' : 'text.primary',
                      display: 'block',
                      fontWeight: location.pathname === '/subscription' ? 600 : 500,
                      position: 'relative',
                      '&::after': location.pathname === '/subscription' ? {
                        content: '""',
                        position: 'absolute',
                        width: '30%',
                        height: '3px',
                        bottom: 0,
                        left: '35%',
                        backgroundColor: primaryColor || 'primary.main',
                        borderRadius: '2px',
                      } : {},
                    }}
                  >
                    Subscription Plans
                  </Button>
                </>
              )}
            </Box>

            {/* User menu or auth buttons */}
            <Box sx={{ flexGrow: 0 }}>
              {user ? (
                <>
                  <Tooltip title="Open settings">
                    <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: primaryColor || 'primary.main',
                          width: 32,
                          height: 32,
                          fontSize: '0.875rem'
                        }}
                      >
                        {user.firstName?.[0]}{user.lastName?.[0]}
                      </Avatar>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    PaperProps={{
                      elevation: 3,
                      sx: {
                        overflow: 'visible',
                        borderRadius: 1,
                        mt: 1.5,
                        boxShadow: '0px 5px 15px rgba(0,0,0,0.1)',
                        '& .MuiAvatar-root': {
                          width: 32,
                          height: 32,
                          ml: -0.5,
                          mr: 1,
                        },
                      },
                    }}
                  >
                    <MenuItem
                      component={RouterLink}
                      to={user.role === 'admin' || user.role === 'superadmin' ? '/admin/dashboard' : '/member/dashboard'}
                      onClick={handleCloseUserMenu}
                    >
                      <Typography textAlign="center">Dashboard</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                      <Typography textAlign="center">Logout</Typography>
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    component={RouterLink}
                    to="/login"
                    sx={{ 
                      color: 'text.primary', 
                      '&:hover': { color: primaryColor || 'primary.main' },
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/register"
                    variant="contained"
                    sx={{ 
                      px: 3, 
                      borderRadius: 28,
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                      background: primaryColor ? 
                        `linear-gradient(90deg, ${primaryColor}, ${adjustColor(primaryColor, 20)})` : 
                        'linear-gradient(90deg, #3b82f6, #6366f1)',
                      transition: 'all 0.3s',
                      '&:hover': {
                        boxShadow: '0 6px 15px rgba(0,0,0,0.15)',
                        transform: 'translateY(-1px)',
                        background: primaryColor ? 
                          `linear-gradient(90deg, ${adjustColor(primaryColor, -10)}, ${adjustColor(primaryColor, 10)})` : 
                          'linear-gradient(90deg, #3579e8, #5a5ef3)',
                      },
                    }}
                  >
                    Sign Up
                  </Button>
                </Box>
              )}
            </Box>
          </Toolbar>
        </Container>
        
        {/* Mobile drawer */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: { width: '75%', maxWidth: 280 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{
                flexGrow: 1,
                fontWeight: 700,
                background: primaryColor ? 
                  `linear-gradient(90deg, ${primaryColor}, ${adjustColor(primaryColor, 20)})` : 
                  'linear-gradient(90deg, #3b82f6, #6366f1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textDecoration: 'none',
              }}
            >
              {tenant ? tenant.name : 'Membership Portal'}
            </Typography>
            <IconButton onClick={handleDrawerToggle}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Divider />
          
          <List sx={{ pt: 1 }}>
            <ListItem disablePadding>
              <ListItemButton
                component={RouterLink}
                to="/"
                sx={{ 
                  borderRadius: 1, 
                  mx: 1,
                  mb: 0.5,
                  ...(location.pathname === '/' && {
                    bgcolor: (theme) => alpha(primaryColor || theme.palette.primary.main, 0.08),
                    color: primaryColor || 'primary.main',
                  })
                }}
              >
                <ListItemText primary="Home" />
              </ListItemButton>
            </ListItem>
            
            {!user ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/login"
                    sx={{ 
                      borderRadius: 1, 
                      mx: 1, 
                      mb: 0.5,
                      '&:hover': {
                        color: primaryColor || 'primary.main',
                      }
                    }}
                  >
                    <ListItemText primary="Login" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/register"
                    sx={{ 
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      bgcolor: primaryColor || 'primary.main',
                      color: 'white',
                      '&:hover': {
                        bgcolor: primaryColor ? adjustColor(primaryColor, -20) : 'primary.dark',
                      },
                    }}
                  >
                    <ListItemText primary="Sign Up" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/register-tenant"
                    sx={{ 
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      color: primaryColor || 'primary.main',
                    }}
                  >
                    <ListItemText primary="Create Organization" />
                  </ListItemButton>
                </ListItem>
              </>
            ) : (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={user.role === 'admin' || user.role === 'superadmin' ? '/admin/dashboard' : '/member/dashboard'}
                    sx={{ 
                      borderRadius: 1, 
                      mx: 1, 
                      mb: 0.5,
                      '&:hover': {
                        color: primaryColor || 'primary.main',
                      }
                    }}
                  >
                    <ListItemText primary="Dashboard" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/subscription"
                    sx={{ 
                      borderRadius: 1, 
                      mx: 1, 
                      mb: 0.5,
                      '&:hover': {
                        color: primaryColor || 'primary.main',
                      }
                    }}
                  >
                    <ListItemText primary="Subscription Plans" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to="/marketing-materials"
                    sx={{ 
                      borderRadius: 1, 
                      mx: 1, 
                      mb: 0.5,
                      '&:hover': {
                        color: primaryColor || 'primary.main',
                      }
                    }}
                  >
                    <ListItemText primary="Marketing Materials" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={handleLogout}
                    sx={{ 
                      borderRadius: 1, 
                      mx: 1, 
                      mb: 0.5
                    }}
                  >
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                      <ListItemText primary="Logout" />
                    </Box>
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
        </Drawer>
      </AppBar>
    </Slide>
  );
};

// Helper function to adjust color for gradients
function adjustColor(color, amount) {
  if (!color) return amount > 0 ? '#6366f1' : '#3b82f6'; // Default gradient colors
  
  // Function to convert hex to RGB
  const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    const fullHex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex);
    
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Function to convert RGB to hex
  const rgbToHex = (r, g, b) => {
    const componentToHex = (c) => {
      const hex = Math.max(0, Math.min(255, c)).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
  };

  const rgb = hexToRgb(color);
  if (!rgb) return color;
  
  return rgbToHex(
    Math.min(255, Math.max(0, rgb.r + amount)),
    Math.min(255, Math.max(0, rgb.g + amount)),
    Math.min(255, Math.max(0, rgb.b + amount))
  );
}

export default Navbar;
